import { RouterProvider, createBrowserRouter, Navigate } from "react-router-dom";
import { useAuth } from "../providers/AuthProvider";
import { ProtectedRoute } from "./ProtectedRoute";
import LoginPage from '../pages/Login';
import LogoutPage from '../pages/Logout';
import Landing from "../pages/Landing";
import Assets from '../pages/assets/Assets';
import Scanner from '../pages/vulns/Scanner';
import ScanResults from '../pages/vulns/ScanResults';
import Audits from '../pages/vulns/Audits';
import VulnDashboard from '../pages/vulns/VulnDashboard';
import VulnScans from '../pages/vulns/VulnScans';
import VulnFindings from '../pages/vulns/VulnFindings';
import VulnAssets from '../pages/vulns/VulnAssets';
import AttackSurface from '../pages/vulns/AttackSurface';
import GrcDashboard from "../pages/grc/GrcDashboard";
import GapAnalysis from "../pages/grc/GapAnalysis";
import Discovery from "../pages/grc/Discovery";
import KnowledgeBase from "../pages/grc/KnowledgeBase";
//import RiskAssessment from "../pages/grc/RiskAssessment";
import Offensive from "../pages/offensive/Offensive";
import OffensiveEngagements from "../pages/offensive/OffensiveEngagements";
import OffensiveDashboard from "../pages/offensive/OffensiveDashboard";
import OffensiveKnowledgeBase from "../pages/offensive/OffensiveKnowledgeBase";
import WirelessTesting from '../pages/offensive/WirelessTesting';
import CreateWirelessEngagementModal from '../components/offensive/CreateWirelessEngagementModal';

import Reports from "../pages/Reports";
import Users from '../pages/admin/Users';
import Orgs from '../pages/admin/Orgs';
import EventViewer from '../pages/admin/EventViewer';
import Security from '../pages/admin/Security';
import VPNClients from '../pages/admin/VPNClients';
import ForgotPassword from '../pages/ForgotPassword';
import ResetPassword from '../pages/ResetPassword';
import Profile from '../pages/settings/Profile';
// Messaging disabled — not core to the platform
// import Messages from '../pages/Messages';
import PageNotFound from '../pages/utility/PageNotFound';

/** subservices.short_name = 'WLAN'. Matches routers/offensive.WIRELESS_SUBTYPE_ID. */
const WIRELESS_SUBTYPE_ID = 20;
// Signup disabled — internal tool, users are created by admins
// import Signup from '../pages/Signup';

const Routes = () => {
  const { token } = useAuth();

  const authRoutes = [
    {
      path: "/",
      element: <ProtectedRoute />,
      children: [
        {
          // The landing page is exploitation status against our own findings.
          // The compliance dashboard that used to live here is now GRC's, where
          // its numbers sit beside the work that produces them.
          path: "/",
          element: <Landing/>,
        },
        {
          path: "/dashboard",
          element: <Landing/>,
        },
        /*
        {
          path: "/assets",
          element: <Assets/>,
        },
        */
        {
          path: "/logout",
          element: <LogoutPage/>
        },
        /*
        {
          path: "/vulns/scanner",
          element: <Scanner/>,
        },
        { path: "/vulns/scanres/:id",
          element: <ScanResults/>,
        },
        {
          path: "/vulns/audits",
          element: <Audits/>,
        },*/
        {
          path: "/vuln/dashboard",
          element: <VulnDashboard/>,
        },
        {
          path: "/vuln/scans",
          element: <VulnScans/>,
        },
        {
          path: "/vuln/findings",
          element: <VulnFindings/>,
        },
        {
          path: "/vuln/assets",
          element: <VulnAssets/>,
        },
        {
          // ASM lives inside Exposure rather than beside it: attack surface
          // management is exposure management, and a second label for one
          // concept confuses the sidebar and the proposal alike.
          path: "/vuln/attack-surface",
          element: <AttackSurface/>,
        },
        {
          path: "/grc/dashboard",
          element: <GrcDashboard/>,
        },
        {
          path: "/grc/discovery",
          element: <Discovery/>,
        },
        {
          path: "/grc/knowledge-base",
          element: <KnowledgeBase/>,
        },
        {
          path: "/grc/gap-analysis",
          element: <GapAnalysis/>,
        },
        {
          path: "/grc/risk-assessment",
          element: null,
        },
        {
          path: "/offensive/dashboard",
          element: <OffensiveDashboard/>,
        },
        {
          path: "/offensive/operations",
          element: <OffensiveEngagements key="offensive"/>,
        },
        {
          path: "/offensive/operations/:engagementId",
          element: <Offensive/>,
        },
        {
          path: "/offensive/wireless",
          // Same list component as the offensive module, over the wireless
          // subtype — a wireless assessment lands on its engagements, not on a
          // console with a dropdown of other people's pentests.
          element: <OffensiveEngagements
            key="wireless"
            subtypeIds={[WIRELESS_SUBTYPE_ID]}
            heading="Wireless Engagements"
            detailPath="/offensive/wireless"
            detailLabel="Wireless testing"
            EngagementModal={CreateWirelessEngagementModal}
          />,
        },
        {
          path: "/offensive/wireless/:engagementId",
          element: <WirelessTesting/>,
        },
        {
          path: "/offensive/knowledge-base",
          element: <OffensiveKnowledgeBase/>,
        },
        // Messaging disabled
        // { path: "/messages", element: <Messages/> },
        {
          path: "/reports",
          element: <Reports/>,
        },
        {
          path: "/settings/profile",
          element: <Profile/>,
        },
        {
          path: "/admin/users",
          element: <Users/>,
        },
        {
          path: "/admin/orgs",
          element: <Orgs/>,
        },
        {
          path: "/admin/events",
          element: <EventViewer/>,
        },
        {
          path: "/admin/security",
          element: <Security/>,
        },
        {
          path: "/admin/vpn",
          element: <VPNClients/>,
        }
      ],
    },
  ];

  const nonAuthRoutes = [
    {
      path: "/login",
      element: <LoginPage/>
    },
    {
      path: "/forgot-password",
      element: <ForgotPassword/>,
    },
    {
      path: "/reset-password/:token",
      element: <ResetPassword/>,
    },
    // Signup disabled — internal tool
    // { path: "/signup", element: <Signup /> }
  ];

  // Add the catch-all 404 route
  const notFoundRoute = {
    path: "*",
    element: token ? <PageNotFound /> : <Navigate to="/login" />
  };

  const router = createBrowserRouter([
    ...(!token ? nonAuthRoutes : []),
    ...authRoutes,
    notFoundRoute // Add this at the end to catch all unmatched routes
  ]);

  return <RouterProvider router={router} />;
};

export default Routes;
